// Template + variables + system values → final subject, HTML (layout + CASL footer) and text.
import type { EmailRuntimeConfig } from "./config.ts";
import { assertCaslFooterPresent, buildCaslFooter } from "./footer.ts";
import { htmlToText } from "./htmlToText.ts";
import { firstUnsafeUrlAttr, markupAllowlistError } from "./htmlTokens.ts";
import { spaceBlocks, wrapInLayout } from "./layout.ts";
import { insertOpenPixel, OPEN_PIXEL_NOTICE } from "./openPixel.ts";
import {
  escapeHtml,
  renderSubject,
  renderTemplate,
  validateTemplateSource,
} from "./render.ts";
import { TZ } from "./sendWindow.ts";
import { isBlankSignature, withSignature } from "./signature.ts";
import {
  defaultIsUnsubscribe,
  type LinkOptions,
  rewriteLinks,
  unsubscribeMatcher,
} from "./trackLinks.ts";
import {
  CASL_ORG,
  type ConsentBasis,
  type Lang,
  type TemplateRow,
} from "./types.ts";

type Person = {
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  phone?: string | null;
  /** GAVION video-campaign: the slug of the rep's ENABLED booking page (booking_settings), else null. */
  booking_slug?: string | null;
};
/** GAVION video-campaign: the public booking pages (53_ booking_settings.slug at /demo/<slug>). */
export const REP_BOOKING_BASE = "https://crm.gavion.ai/demo/";
const BOOKING_SLUG_RE = /^[a-z0-9](?:[a-z0-9-]{0,38}[a-z0-9])?$/;
/** {{rep.booking_url}}: the rep's booking page, "" without one (hidden by {{#if rep.booking_url}}). */
export const repBookingUrl = (slug: string | null | undefined): string =>
  typeof slug === "string" && BOOKING_SLUG_RE.test(slug)
    ? `${REP_BOOKING_BASE}${slug}`
    : "";
export type VarsInput = {
  company?: {
    name?: string | null;
    city?: string | null;
    trade?: string | null;
    trade_label?: string | null;
  } | null;
  contact?: { first_name?: string | null } | null;
  rep?: Person | null;
  deal?: { demo_at?: string | null; teams_join_url?: string | null } | null;
};

const DEMO_FMT: Record<Lang, Intl.DateTimeFormat> = {
  fr: new Intl.DateTimeFormat("fr-CA", {
    timeZone: TZ,
    weekday: "long",
    day: "numeric",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
  }),
  en: new Intl.DateTimeFormat("en-CA", {
    timeZone: TZ,
    weekday: "long",
    day: "numeric",
    month: "long",
    hour: "numeric",
    minute: "2-digit",
  }),
};
/** Demo date in Toronto time; "" for an invalid date (the template's {{#if}} then hides it). */
export function formatDemoAt(iso: string, lang: Lang): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : DEMO_FMT[lang].format(d);
}

export function buildTemplateVars(
  i: VarsInput,
  cfg: EmailRuntimeConfig,
): Record<string, unknown> {
  const rep = i.rep
    ? {
        first_name: i.rep.first_name ?? "",
        full_name: `${i.rep.first_name ?? ""} ${i.rep.last_name ?? ""}`.trim(),
        email: i.rep.email ?? cfg.replyTo,
        phone: i.rep.phone ?? "",
        booking_url: repBookingUrl(i.rep.booking_slug), // GAVION video-campaign
      }
    : {
        first_name: cfg.fromName,
        full_name: cfg.fromName,
        email: cfg.replyTo,
        phone: "",
        booking_url: "",
      };
  const demoAt = i.deal?.demo_at;
  return {
    company: {
      name: i.company?.name || undefined,
      city: i.company?.city ?? "",
      trade_label: i.company?.trade_label ?? i.company?.trade ?? "",
    },
    contact: { first_name: i.contact?.first_name || undefined },
    rep,
    deal: {
      demo_at_fr: demoAt ? formatDemoAt(demoAt, "fr") : "",
      demo_at_en: demoAt ? formatDemoAt(demoAt, "en") : "",
      teams_join_url: i.deal?.teams_join_url ?? "",
    },
    // GAVION missed-call: Paramètres → IA « Lien de la brochure » (https or ""); hidden by {{#if brochure_url}}.
    brochure_url: cfg.brochureUrl ?? "",
  };
}

const SAMPLE_DEMO_AT = "2026-10-20T14:00:00Z";
export const SAMPLE_VARS: Record<string, unknown> = Object.freeze({
  company: {
    name: "Plomberie Exemple inc.",
    city: "Montréal",
    trade_label: "Plomberie",
  },
  contact: { first_name: "Marie" },
  rep: {
    first_name: "Martin",
    full_name: "Martin Exemple",
    email: "martin@gavion.ai",
    phone: "+1 514 555-0100",
    booking_url: "https://crm.gavion.ai/demo/martin",
  },
  deal: {
    demo_at_fr: formatDemoAt(SAMPLE_DEMO_AT, "fr"),
    demo_at_en: formatDemoAt(SAMPLE_DEMO_AT, "en"),
    teams_join_url: "https://teams.microsoft.com/l/meetup-join/exemple",
  },
  brochure_url: "https://gavion.ai/brochure.pdf",
}) as Record<string, unknown>;

export type ComposeSystem = {
  unsubscribeUrl: string;
  consentBasis: ConsentBasis | null;
  config: EmailRuntimeConfig;
  /** Click-tracking map (pre-signed by the caller); null keeps the link. Never sees the unsubscribe link. */
  trackLink?: (href: string) => string | null;
  /**
   * GAVION open-pixel: signed pixel URL (buildOpenPixelUrl). When set, the pixel is inserted after
   * every allowlist check (a template can never carry it) and the Law 25 notice joins the footer.
   */
  openPixelUrl?: string;
  /**
   * GAVION campaign-fixes: the sending rep's signature, ALREADY sanitised by the caller (gavion-graph-send's
   * sanitizeEmailHtml, as for a 1:1 email). Never rendered as a template; placed after the body, before the footer.
   */
  signatureHtml?: string;
};
export type ComposedEmail = {
  subject: string;
  preheader: string;
  html: string;
  text: string;
  missing: string[];
  hasFooter: boolean;
};

export const snippetOf = (text: string): string =>
  text.replace(/\s+/g, " ").trim().slice(0, 280);

/** Link options that exclude the real unsubscribe endpoint (origin + path of the unsubscribe URL). */
export function unsubscribeLinkOptions(unsubscribeUrl: string): LinkOptions {
  try {
    const u = new URL(unsubscribeUrl);
    return { isUnsubscribe: unsubscribeMatcher(`${u.origin}${u.pathname}`) };
  } catch {
    return { isUnsubscribe: defaultIsUnsubscribe };
  }
}

/**
 * Fail-closed source checks before rendering, for every kind: the body must validate (structural
 * allowlist, URL attributes, syntax) and no unfilled `[[…]]` drafting placeholder may remain in the
 * subject, preheader, body or text body.
 */
function assertSendableSource(t: TemplateRow): void {
  const v = validateTemplateSource(t.body_html, { kind: t.kind });
  if (!v.ok) throw new Error(v.code);
  const sources = [
    t.subject,
    t.preheader ?? "",
    t.body_html,
    t.body_text ?? "",
  ];
  if (sources.some((s) => s.includes("[[")))
    throw new Error("unfilled_placeholder");
}

export function composeEmail(
  t: TemplateRow,
  vars: Record<string, unknown>,
  sys: ComposeSystem,
): ComposedEmail {
  assertSendableSource(t);
  const ctx = { ...vars, unsubscribe_url: sys.unsubscribeUrl };
  const subject = renderSubject(t.subject, ctx);
  const pre = renderSubject(t.preheader ?? "", ctx);
  const body = renderTemplate(t.body_html, ctx);
  const bodyText = t.body_text
    ? renderTemplate(t.body_text, ctx, { escape: false })
    : null;
  const footer = buildCaslFooter({
    kind: t.kind,
    lang: t.language,
    legalName: CASL_ORG.legalName,
    postalAddress: sys.config.postalAddress,
    contactEmail: sys.config.contactEmail,
    consentBasis: sys.consentBasis,
    unsubscribeUrl: sys.unsubscribeUrl,
  });
  // GAVION campaign-fixes: the rep's signature joins the body (1:1 rule, signature.ts), before the CASL footer.
  const signature = isBlankSignature(sys.signatureHtml)
    ? ""
    : (sys.signatureHtml ?? "");
  const bodyHtml = withSignature(body.output, signature);
  // Re-check the rendered body (every kind): a {{#if}} branch can leave a tag open even when the source balances.
  const markupErr = markupAllowlistError(bodyHtml);
  if (markupErr) throw new Error(markupErr.code);
  const preheader = pre.output.replace(/\s{2,}/g, " ").trim();
  // GAVION open-pixel: the notice travels with the pixel only (Law 25 transparency).
  const notice = sys.openPixelUrl ? OPEN_PIXEL_NOTICE[t.language] : null;
  const noticeHtml = notice
    ? `<p style="margin:${footer ? "8px" : "0"} 0 0 0;">${escapeHtml(notice)}</p>`
    : "";
  const laidOut = wrapInLayout({
    // GAVION 2026-10-30: block spacing after the allowlist check (style attributes are not template markup)
    bodyHtml: withSignature(spaceBlocks(body.output), signature),
    preheader,
    footerHtml: footer || notice ? `${footer?.html ?? ""}${noticeHtml}` : null,
    lang: t.language,
    logoUrl: sys.config.logoUrl,
  });
  if (firstUnsafeUrlAttr(laidOut) !== null) throw new Error("unsafe_url_attr");
  const tracked = sys.trackLink
    ? rewriteLinks(
        laidOut,
        sys.trackLink,
        unsubscribeLinkOptions(sys.unsubscribeUrl),
      )
    : laidOut;
  // GAVION open-pixel: inserted last, after the allowlist and link rewriting (never user-supplied).
  const html = sys.openPixelUrl
    ? insertOpenPixel(tracked, sys.openPixelUrl)
    : tracked;
  // Tracked URLs come from the caller; re-check the final HTML as well.
  if (firstUnsafeUrlAttr(html) !== null) throw new Error("unsafe_url_attr");
  const text = [
    bodyText ? bodyText.output.trim() : htmlToText(body.output),
    signature ? htmlToText(signature) : null,
    footer?.text,
    notice,
  ]
    .filter(Boolean)
    .join("\n\n");
  // Send-path guard on the final parts (after link rewriting): throws casl_footer_missing.
  assertCaslFooterPresent(html, text, t.kind, {
    unsubscribeUrl: sys.unsubscribeUrl,
    postalAddress: sys.config.postalAddress,
    legalName: CASL_ORG.legalName,
  });
  const missing = [
    ...new Set([
      ...subject.missing,
      ...pre.missing,
      ...body.missing,
      ...(bodyText?.missing ?? []),
    ]),
  ];
  return {
    subject: subject.output.replace(/\s{2,}/g, " ").trim(),
    preheader,
    html,
    text,
    missing,
    hasFooter: footer !== null,
  };
}
